import React from "react";
import {
  BLEED, C, FONT, clamp, lerp, seg, smooth, ease, mixColor, rng, wire,
  Label, Worker, workerJoints, mixPose,
} from "../kit";

// Act I · The Gold Watch (the spoken intro).
// A mill town in hard times; thirty years on Line B; a watch, and the first
// cold light in the piece. Beats (p):
//   0-.31    dawn: the textile mill is chained shut, one plant stack still
//            smokes, and he walks in through GATE 2 with his pail
//   .29-.7   thirty years at his station: his cards stamp 1995 -> 2025 while
//            days and seasons cycle through the roof glass, the stations
//            around him go dark, and he slumps a little more at each beat
//   .68-1    his bench: an open box with a gold watch. He is gone, his lamp
//            dies, and a terminal flickers on where he stood.
// Every SVG id starts with "gw-".

const keyed = (keys, k, i) => {
  if (k <= keys[0][0]) return keys[0][i];
  for (let j = 1; j < keys.length; j++) {
    if (k <= keys[j][0]) {
      const a = keys[j - 1];
      return mixColor(a[i], keys[j][i], (k - a[0]) / (keys[j][0] - a[0]));
    }
  }
  return keys[keys.length - 1][i];
};

// Clockwise, like the house outlines, so overlaps stay filled.
const circ = (cx, cy, r) =>
  `M ${cx - r} ${cy} a ${r} ${r} 0 1 1 ${2 * r} 0 a ${r} ${r} 0 1 1 ${-2 * r} 0`;

const deg = (rad) => (rad * 180) / Math.PI;
const frac = (v) => v - Math.floor(v);

// ---------------------------------------------------------------------------
// The man ages by degrees: his poses here are explicit angle sets, blended
// with the kit's mixPose and drawn by the kit Worker.
// ---------------------------------------------------------------------------

// Hammering at the bench, young (age 0) to stooped (age 1). Both strike the
// bench top where the far hand holds the work, ~95 units up.
const workPose = (phase, age) => {
  const s = Math.sin(phase);
  const young = {
    lean: 0.24, head: 0.32,
    legs: [[-0.1, -0.1], [0.12, 0.12]],
    arms: [[0.7, 1.3], [0.75 + 0.35 * s, 2.025 + 0.575 * s]],
  };
  const old = {
    lean: 0.46, head: 0.6,
    legs: [[0.02, -0.2], [0.22, 0.02]],
    arms: [[0.3, 1.67], [0.22 + 0.22 * s, 2.1 + 0.4 * s]],
  };
  return mixPose(young, old, age);
};
// Done: arms down, head down.
const REST = {
  lean: 0.42, head: 0.74,
  legs: [[0.02, -0.12], [0.14, 0]],
  arms: [[0.1, 0.06], [0.2, 0.14]],
};

// ===========================================================================
// 1 · The town at dawn
// ===========================================================================

const GY = 640; // the horizon, and the road he walks along
const MIRROR = GY + 5; // the river reflects the town about this line
const WATER_Y = GY + 10;
const SUN_X = 650;
const SUN_R = 58;
const FIGURE = "#050404";
const BANK = "#050403";
// Body travel per radian of walk phase at s=1: tying phase to distance keeps
// the feet from skating whatever the act length.
const STRIDE = 21.3;
const WHISTLE_P = 0.1;

// Sky over dawn 0 -> 1: [k, top, mid, low, horizon].
const SKY = [
  [0.0, "#020306", "#03050a", "#060912", "#0a0e19"],
  [0.25, "#03060d", "#08101f", "#151c33", "#35283a"],
  [0.5, "#071023", "#13233f", "#363a58", "#9a5a40"],
  [0.75, "#0c1a32", "#203657", "#625b6d", "#dc8b4f"],
  [1.0, "#11233f", "#2b4566", "#81727f", "#eea45f"],
];

const STARS = (() => {
  const r = rng(1101);
  return Array.from({ length: 130 }, (_, i) => {
    const tall = i >= 80; // the band above the box that only tall screens show
    const x = tall ? -380 + r() * 2360 : -60 + r() * 1720;
    const y = tall ? -1380 + r() * 1370 : 8 + Math.pow(r(), 1.15) * 470;
    const h = clamp((560 - y) / 700);
    return {
      x, y,
      r: 0.55 + Math.pow(r(), 3) * 1.5,
      a: 0.3 + r() * 0.7,
      rate: 0.7 + r() * 2.4,
      ph: r() * 6.283,
      // the dawn level at which this star has faded
      f0: (0.02 + 0.2 * h) / 0.78 + 0.12,
    };
  });
})();

// Streaky dawn cloud banks: [x, y, width, thickness, drift per second].
const CLOUDS = [
  [40, 318, 600, 12, 2.2],
  [760, 258, 420, 9, 1.6],
  [1040, 382, 640, 13, 2.6],
  [300, 452, 380, 7, 1.4],
  [1180, 190, 380, 7, 1.9],
  [-240, 172, 460, 7, 1.2],
];
const CLOUD_D = (() => {
  const r = rng(2024);
  return CLOUDS.map(([x, y, w, h]) => {
    const strands = [];
    const n = 3 + Math.floor(r() * 3);
    for (let i = 0; i < n; i++) {
      const sx = x + r() * w * 0.4;
      const sw = w * (0.3 + r() * 0.6);
      const sy = y + (r() - 0.5) * h * 2.2;
      const sh = h * (0.3 + r() * 0.55);
      const pk = 0.25 + r() * 0.45;
      strands.push(
        `M ${sx} ${sy} C ${sx + sw * pk * 0.5} ${sy - sh} ${sx + sw * pk} ${sy - sh * 1.1} ${sx + sw} ${sy + sh * 0.1}` +
        ` C ${sx + sw * 0.7} ${sy + sh * 0.5} ${sx + sw * 0.3} ${sy + sh * 0.45} ${sx} ${sy} Z`
      );
    }
    return strands.join(" ");
  });
})();

const TREELINE = (() => {
  const r = rng(303);
  let d = `M -420 ${GY + 4} L -420 ${GY - 6}`;
  for (let x = -420; x < 2020; x += 22) {
    const h = 5 + r() * 8 + (Math.sin(x * 0.011) + 1) * 4;
    d += ` Q ${x + 11} ${GY - h - 7} ${x + 22} ${GY - 4 - r() * 5}`;
  }
  return d + ` L 2020 ${GY + 4} Z`;
})();

// [x, width, wall, roof, chimney]; every house stands on the horizon.
const HOUSES = [
  [62, 50, 24, 17, 1], [124, 40, 19, 14, 0], [176, 62, 27, 12, 0], [250, 38, 20, 15, 1],
  [590, 42, 20, 15, 0], [640, 50, 23, 17, 1], [702, 38, 17, 12, 0], [880, 50, 24, 17, 1],
  [940, 42, 20, 14, 0], [990, 54, 26, 18, 0],
];
// Only a few kitchens are up: [house, x along the wall, on at (s), off at (s)].
const LIT = [[0, 0.3, -1, 99], [2, 0.62, 1.8, 99], [5, 0.66, -1, 7.5], [7, 0.3, 2.6, 99], [9, 0.6, 4.4, 99]];
const TREES = [[108, 13], [236, 17], [574, 12], [686, 11], [858, 16], [1062, 14], [26, 15]];

const TOWN_D = (() => {
  const parts = HOUSES.map(([x, w, wall, roof, chim]) => {
    const top = GY - wall;
    let d = `M ${x} ${GY + 1} L ${x} ${top}`;
    d += roof ? ` L ${x + w / 2} ${top - roof} L ${x + w} ${top}` : ` L ${x + w} ${top}`;
    d += ` L ${x + w} ${GY + 1} Z`;
    if (chim) {
      const cx = x + w * 0.7;
      d += ` M ${cx} ${top - roof * 0.3} V ${top - roof * 0.6 - 8} h 6 V ${top - roof * 0.3} Z`;
    }
    return d;
  });
  TREES.forEach(([x, r]) => {
    parts.push(circ(x, GY - r - 5, r));
    parts.push(circ(x - r * 0.65, GY - r * 0.7 - 3, r * 0.7));
    parts.push(circ(x + r * 0.6, GY - r * 0.75 - 3, r * 0.72));
    parts.push(`M ${x - 1.5} ${GY + 1} V ${GY - 8} h 3 V ${GY + 1} Z`);
  });
  // Church: nave, tower, spire.
  parts.push(`M 790 ${GY + 1} V 606 L 802 592 L 858 592 L 870 606 V ${GY + 1} Z`);
  parts.push(`M 762 ${GY + 1} V 560 H 794 V ${GY + 1} Z`);
  parts.push(`M 760 562 L 778 448 L 796 562 Z`);
  // Water tower tank.
  parts.push(`M 292 506 L 292 458 L 334 440 L 376 458 L 376 506 Q 334 522 292 506 Z`);
  return parts.join(" ");
})();

const TOWER_D = [
  "M 300 508 L 286 640 M 318 512 L 312 640 M 350 512 L 356 640 M 368 508 L 382 640",
  "M 334 520 L 334 640 M 334 440 L 334 430",
  "M 284 511 L 384 511",
].join(" ");
const TOWER_BRACE_D = [
  "M 296 552 L 315 594 M 315 552 L 296 594 M 352 552 L 371 594 M 371 552 L 352 594",
  "M 294 552 L 374 552 M 291 594 L 377 594",
  "M 778 448 L 778 436",
].join(" ");

// --- the textile mill, shut ------------------------------------------------------
// Four storeys, a stair tower with an open belfry and a round stack lettered
// down its side. Its window rows are empty, so the dawn shows through them.

const MILL = { x0: 398, x1: 580, top: GY - 94 };
const MILL_D = [
  `M ${MILL.x0} ${GY + 1} V ${MILL.top} H ${MILL.x1} V ${GY + 1} Z`,
  `M ${MILL.x0 - 3} ${MILL.top + 3} V ${MILL.top - 1} H ${MILL.x1 + 3} V ${MILL.top + 3} Z`,
  `M 462 ${GY + 1} V 496 H 492 V ${GY + 1} Z`,
  `M 458 497 L 477 477 L 496 497 Z`,
  `M 551 ${MILL.top + 1} L 555 382 L 569 382 L 573 ${MILL.top + 1} Z`,
  `M 552 383 V 373 H 572 V 383 Z`,
].join(" ");
const BELFRY_D = "M 471 478 V 463 H 483 V 478 Z M 474 478 V 470 Q 477 466 480 470 V 478 Z M 468 464 L 477 452 L 486 464 Z";
const MILL_WIN = (() => {
  const r = rng(2231);
  const open = [];
  const shut = [];
  for (let row = 0; row < 4; row++) {
    for (let x = 404; x < MILL.x1 - 6; x += 12.5) {
      if (x + 7 > 458 && x < 496) continue; // the stair tower
      if (x + 7 > 549 && row === 0) continue; // the stack's footing
      const y = MILL.top + 13 + row * 20;
      (r() < 0.6 ? open : shut).push(`M ${x} ${y} h 7 v 12 h -7 Z`);
    }
  }
  for (let y = 506; y < 620; y += 22) open.push(`M 475 ${y} h 4 v 11 h -4 Z`);
  return { open: open.join(" "), shut: shut.join(" ") };
})();
const MILL_FENCE_TOP = GY - 30;
const MILL_GATE = { l: 496, r: 544 };
const MILL_FENCE_D = (() => {
  const d = [];
  for (let x = 392; x <= 592; x += 40) {
    if (x > MILL_GATE.l - 4 && x < MILL_GATE.r + 4) continue;
    d.push(`M ${x} ${GY + 1} V ${MILL_FENCE_TOP - 2}`);
  }
  d.push(`M 392 ${MILL_FENCE_TOP} H ${MILL_GATE.l} M ${MILL_GATE.r} ${MILL_FENCE_TOP} H 592`);
  return d.join(" ");
})();
const MILL_GATE_D =
  `M ${MILL_GATE.l} ${MILL_FENCE_TOP - 4} H 519 V ${GY} H ${MILL_GATE.l} Z ` +
  `M 521 ${MILL_FENCE_TOP - 4} H ${MILL_GATE.r} V ${GY} H 521 Z`;

// --- the auto plant ------------------------------------------------------------------

const PLANT_X = 1150;
const EAVE = GY - 78;
const TOOTH = 64;
const TOOTH_H = 34;
const TEETH = 14;
const PLANT_D = (() => {
  let d = `M ${PLANT_X} ${GY + 1}`;
  for (let i = 0; i < TEETH; i++) {
    const x = PLANT_X + i * TOOTH;
    d += ` L ${x} ${EAVE - TOOTH_H} L ${x + TOOTH} ${EAVE}`;
  }
  return d + ` L ${PLANT_X + TEETH * TOOTH} ${GY + 1} Z`;
})();
// Sawtooth clerestory glass, seen just off edge-on.
const GLAZING = Array.from({ length: TEETH }, (_, i) => {
  const x = PLANT_X + i * TOOTH;
  const y0 = EAVE - TOOTH_H + 3;
  return `M ${x + 1.5} ${y0} L ${x + 9} ${y0 + 4} L ${x + 9} ${EAVE - 2} L ${x + 1.5} ${EAVE - 2} Z`;
});
const DOOR = { x: 1324, w: 24, y: GY - 42 };
const FACADE_WIN_D = (() => {
  const d = [];
  for (let x = 1172; x < 2000; x += 46) {
    if (x + 14 > DOOR.x - 6 && x < DOOR.x + DOOR.w + 6) continue;
    d.push(`M ${x} 584 h 14 v 30 h -14 Z`);
  }
  return d.join(" ");
})();
// The left stack still smokes; the right one has gone cold.
const STACKS = [
  { x: 1296, top: 290, w0: 36, w1: 26, ph: 0 },
  { x: 1410, top: 320, w0: 30, w1: 22, ph: 0.47 },
];
const STACK_D = STACKS.map(({ x, top, w0, w1 }) =>
  `M ${x - w0 / 2} ${EAVE + 4} L ${x - w1 / 2} ${top} L ${x + w1 / 2} ${top} L ${x + w0 / 2} ${EAVE + 4} Z`
).join(" ");
const WHISTLE = { x: 1346, y: 529 };
// Rooftop sign: two lines of letters standing on rails against the sky.
const SIGN = { x: 1222, y: 514 };
const SIGN_D =
  `M 1182 545 V ${SIGN.y + 4} M 1270 558 V ${SIGN.y + 4} M 1172 ${SIGN.y + 4} H 1280 M 1180 ${SIGN.y + 21} H 1272`;

// Fence along the far side of the road; the gate opens between two posts.
const FENCE_X0 = 1040;
const FENCE_TOP = GY - 56;
const GATE_L = 1092;
const GATE_R = 1172;
const GATE3 = { l: 1400, r: 1460 };
const FENCE_POSTS_D = (() => {
  const d = [];
  for (let x = FENCE_X0; x < 2010; x += 60) {
    if (x > GATE_L - 20 && x < GATE_R + 20) continue;
    d.push(`M ${x} ${GY + 1} V ${FENCE_TOP - 2} l -7 -9`);
  }
  d.push(`M ${FENCE_X0} ${FENCE_TOP} H ${GATE_L} M ${GATE_R} ${FENCE_TOP} H 2010`);
  d.push(`M ${FENCE_X0 - 7} ${FENCE_TOP - 9} H ${GATE_L - 10} M ${GATE_R + 10} ${FENCE_TOP - 9} H 2010`);
  return d.join(" ");
})();
const GATE_LEAVES_D =
  `M ${GATE_L} ${FENCE_TOP} L ${GATE_L + 24} ${FENCE_TOP - 4} L ${GATE_L + 24} ${GY - 3} L ${GATE_L} ${GY} Z ` +
  `M ${GATE_R} ${FENCE_TOP} L ${GATE_R - 22} ${FENCE_TOP - 4} L ${GATE_R - 22} ${GY - 3} L ${GATE_R} ${GY} Z`;
// Gate 3 is shut: both leaves closed across the opening.
const GATE3_D =
  `M ${GATE3.l + 2} ${FENCE_TOP - 2} H ${GATE3.l + 29} V ${GY - 1} H ${GATE3.l + 2} Z ` +
  `M ${GATE3.l + 31} ${FENCE_TOP - 2} H ${GATE3.r - 2} V ${GY - 1} H ${GATE3.l + 31} Z`;

// --- road and river ------------------------------------------------------------------

const POLES = [180, 480, 780, 1030];
const ARM_Y = 414;
const POLE_D = POLES.map((x) =>
  `M ${x - 3} ${GY + 3} L ${x - 2} ${ARM_Y - 12} L ${x + 2} ${ARM_Y - 12} L ${x + 3} ${GY + 3} Z ` +
  `M ${x - 29} ${ARM_Y} h 58 v 4 h -58 Z ` +
  `M ${x - 26} ${ARM_Y} v -6 h 4 v 6 Z M ${x + 22} ${ARM_Y} v -6 h 4 v 6 Z M ${x - 2} ${ARM_Y - 12} v -5 h 4 v 5 Z`
).join(" ") + ` M 474 470 h 12 v 22 h -12 Z`;
const WIRE_D = (() => {
  const d = [];
  for (let i = 0; i < POLES.length - 1; i++) {
    const a = POLES[i];
    const b = POLES[i + 1];
    d.push(wire(a - 24, ARM_Y - 6, b - 24, ARM_Y - 6, 13));
    d.push(wire(a + 24, ARM_Y - 6, b + 24, ARM_Y - 6, 15));
    d.push(wire(a, ARM_Y - 17, b, ARM_Y - 17, 11));
    d.push(wire(a + 3, 474, b - 3, 474, 18));
  }
  d.push(wire(1054, ARM_Y - 6, PLANT_X + 4, EAVE - 20, 10));
  d.push(wire(1006, ARM_Y - 6, PLANT_X + 4, EAVE - 14, 14));
  d.push(wire(-420, ARM_Y - 4, 156, ARM_Y - 6, 22));
  d.push(wire(-420, ARM_Y - 2, 204, ARM_Y - 6, 22));
  return d.join(" ");
})();

// Birds on the top-left wire between the second and third poles.
const PERCH = { x0: 456, x1: 756, y: ARM_Y - 6, sag: 13 };
const BIRDS = (() => {
  const r = rng(808);
  return [0.18, 0.27, 0.35, 0.49, 0.58, 0.7, 0.79].map((u) => ({
    bx: lerp(PERCH.x0, PERCH.x1, u),
    by: PERCH.y + 4 * u * (1 - u) * PERCH.sag,
    face: r() > 0.4 ? 1 : -1,
    delay: r() * 0.55,
    vx: -95 + r() * 120,
    vy: 70 + r() * 45,
    flap: 17 + r() * 7,
    bob: r() * 6.28,
    look: 0.2 + r() * 0.5,
  }));
})();

const SMOKE = (() => {
  const r = rng(4501);
  return Array.from({ length: 30 }, (_, i) => ({
    off: i / 30 + r() * 0.015,
    rise: 0.85 + r() * 0.3,
    size: 0.8 + r() * 0.45,
    wob: r() * 6.28,
  }));
})();
const SMOKE_LIFE = 11;

const STEAM = (() => {
  const r = rng(77);
  return Array.from({ length: 16 }, (_, i) => ({ at: i * 0.1, dx: -5 + r() * 10, size: 0.8 + r() * 0.5 }));
})();

// Ripple rows spread out toward the viewer; dashes drift with the current.
const RIPPLES = (() => {
  const r = rng(616);
  const rows = [];
  let y = WATER_Y;
  for (let i = 0; i < 30; i++) {
    y += 2.2 + i * 0.95;
    const a = 40 + r() * 160;
    const b = 6 + r() * 30;
    const c = 20 + r() * 90;
    const e = 4 + r() * 24;
    rows.push({ y, w: 0.7 + i * 0.07, dash: `${a} ${b} ${c} ${e}`, v: (r() - 0.35) * 16, op: 0.35 + r() * 0.35 });
  }
  return rows;
})();
const GLINTS = (() => {
  const r = rng(515);
  return Array.from({ length: 22 }, (_, i) => {
    const k = i / 21;
    return {
      y: WATER_Y + 4 + Math.pow(k, 1.3) * 190,
      w: 6 + r() * (16 + 34 * k),
      dx: (r() - 0.5) * (18 + 70 * k),
      rate: 1.3 + r() * 3.2,
      ph: r() * 6.28,
    };
  });
})();
const REEDS_D = (() => {
  const r = rng(919);
  const d = [];
  for (let i = 0; i < 60; i++) {
    const left = i % 2 === 0;
    const x = left ? -120 + r() * 360 : 1330 + r() * 420;
    const h = 60 + r() * 150 * (left ? 1 - (x + 120) / 520 : (x - 1330) / 420 + 0.3);
    const lean = -12 + r() * 24;
    d.push(`M ${x} 930 Q ${x + lean * 0.3} ${930 - h * 0.6} ${x + lean} ${930 - h}`);
  }
  return d.join(" ");
})();
const CATTAILS = [[44, 806], [118, 828], [186, 842], [1392, 838], [1468, 808], [1530, 826]];

function walker(p, a, b, x0, x1, s) {
  const x = lerp(x0, x1, seg(p, a, b));
  return { x, phase: (x - x0) / (STRIDE * s) };
}

// A chain across a shut gate, its padlock, and the plate hung from it.
function Shut({ x, y, ink, plate, s = 1 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M -13 -7 Q 0 3 13 -7" stroke={ink} strokeWidth={1.7} strokeDasharray="2.2 1.1" fill="none" />
      <path d="M -2.4 -1.5 V -4 A 2.4 2.4 0 0 1 2.4 -4 V -1.5" stroke={ink} strokeWidth={1.3} fill="none" />
      <rect x={-3.6} y={-1.8} width={7.2} height={6.2} rx={1} fill={ink} />
      <path d="M -20 9 L -14 3 M 20 9 L 14 3" stroke={ink} strokeWidth={0.9} />
      <rect x={-25} y={8} width={50} height={14} fill={plate} />
      <text x={0} y={18.4} fill="#1d1813" fontFamily={FONT.mono} fontSize={9.5} fontWeight={600}
        letterSpacing="0.14em" textAnchor="middle">CLOSED</text>
    </g>
  );
}

function Town({ p, local, dur }) {
  // Dawn runs from first light (sun just touching the horizon) to full sunrise.
  const dawn = lerp(0.34, 0.72, seg(p, 0, 0.3));
  const top = keyed(SKY, dawn, 1);
  const mid = keyed(SKY, dawn, 2);
  const low = keyed(SKY, dawn, 3);
  const hor = keyed(SKY, dawn, 4);
  const farInk = mixColor(low, "#000000", 0.58);
  const townInk = mixColor(low, "#000000", 0.74);
  const plantInk = mixColor(low, "#000000", 0.86);
  const nearInk = mixColor(low, "#000000", 0.92);

  const sunY = GY + SUN_R - 330 * (0.78 * dawn - 0.18);
  const glow = smooth(seg(dawn, 0.04, 0.77));
  const glowY = Math.max(sunY, GY - 20);
  const sunUp = seg(sunY, GY + SUN_R, GY - SUN_R);
  const starFade = (s) => 1 - smooth(seg(dawn, s.f0 - 0.2, s.f0));

  const whistleAt = WHISTLE_P * dur;
  const sinceWhistle = local - whistleAt;
  const plantLit = 0.35 + 0.55 * smooth(seg(sinceWhistle, 0, 1.6));
  const gateLamp = p < 0.2 ? 1 : p < 0.212 ? (Math.sin(local * 60) > 0 ? 0.6 : 0.1) : 0;

  const cloudLit = mixColor(mixColor("#0b0e17", "#b56a5a", seg(dawn, 0.12, 0.5)), "#ffc78e", seg(dawn, 0.5, 1));
  const cloudShade = mixColor(mid, "#0a0c14", 0.45);
  const plate = mixColor("#3b352e", "#d9cfbc", 0.25 + 0.75 * seg(dawn, 0.3, 0.7));

  // Two men talk under the gate lamp; when the whistle goes they head in. He
  // walks out of the sunrise and follows them through Gate 2.
  const S = 0.58;
  const him = walker(p, 0, 0.31, 600, 1262, S);
  const firstGo = p >= WHISTLE_P;
  const secondGo = p >= WHISTLE_P + 0.008;
  const first = firstGo ? walker(p, WHISTLE_P, 0.2, 1104, 1336, 0.6) : { x: 1104, phase: 0 };
  const second = secondGo ? walker(p, WHISTLE_P + 0.008, 0.225, 1068, 1336, 0.57) : { x: 1068, phase: 0 };
  const doorFade = (x) => 1 - seg(x, DOOR.x - 34, DOOR.x + 6);
  const workers = [
    { key: "second", x: second.x, s: 0.57, hat: "hard", facing: 1, pose: secondGo ? "walk" : "stand", phase: second.phase },
    { key: "first", x: first.x, s: 0.6, hat: "cap", facing: firstGo ? 1 : -1, pose: firstGo ? "walk" : "stand", phase: first.phase },
    { key: "him", x: him.x, s: S, hat: "cap", pail: true, facing: 1, pose: "walk", phase: him.phase },
  ]
    .map((w) => ({ ...w, opacity: doorFade(w.x) }))
    .filter((w) => w.opacity > 0.01);
  const inside = workers.filter((w) => w.x >= 1120);
  const outside = workers.filter((w) => w.x < 1120);
  const drawWorker = (w) => (
    <Worker key={w.key} x={w.x} y={GY} s={w.s} color={FIGURE} facing={w.facing} pose={w.pose}
      phase={w.phase} hat={w.hat} pail={!!w.pail} opacity={w.opacity} />
  );

  // A cigarette, flicked when the whistle goes.
  const hand = workerJoints({ x: 1104, y: GY, s: 0.6, facing: -1, pose: "stand" }).hand;
  const ember = 0.65 + 0.35 * Math.sin(local * 1.7) * Math.sin(local * 0.6 + 1);
  const flick = clamp(sinceWhistle / 0.5);

  // The camera leans in on Gate 2 as he goes through it.
  const push = ease.inOut(seg(p, 0.17, 0.32));
  const zoom = 1 + 0.6 * push;
  const ax = lerp(1132, 880, push);
  const ay = lerp(600, 560, push);

  return (
    <g transform={`translate(${ax} ${ay}) scale(${zoom}) translate(-1132 -600)`}>
      <defs>
        <linearGradient id="gw-sky" gradientUnits="userSpaceOnUse" x1="0" y1="-300" x2="0" y2={GY}>
          <stop offset="0" stopColor={top} />
          <stop offset="0.45" stopColor={mid} />
          <stop offset="0.8" stopColor={low} />
          <stop offset="1" stopColor={hor} />
        </linearGradient>
        <linearGradient id="gw-sky-r" gradientUnits="userSpaceOnUse" x1="0" y1={2 * MIRROR - GY} x2="0" y2={2 * MIRROR + 300}>
          <stop offset="0" stopColor={hor} />
          <stop offset="0.2" stopColor={low} />
          <stop offset="0.55" stopColor={mid} />
          <stop offset="1" stopColor={top} />
        </linearGradient>
        <linearGradient id="gw-deep" gradientUnits="userSpaceOnUse" x1="0" y1={WATER_Y} x2="0" y2={WATER_Y + 240}>
          <stop offset="0" stopColor="#000000" stopOpacity="0.22" />
          <stop offset="0.6" stopColor="#000000" stopOpacity="0.6" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.84" />
        </linearGradient>
        <radialGradient id="gw-glow">
          <stop offset="0" stopColor="#ffe6bd" stopOpacity="0.95" />
          <stop offset="0.1" stopColor={C.warmHot} stopOpacity="0.7" />
          <stop offset="0.3" stopColor={C.warm} stopOpacity="0.32" />
          <stop offset="0.62" stopColor={C.warmDeep} stopOpacity="0.1" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="gw-sun">
          <stop offset="0" stopColor="#fff6e4" />
          <stop offset="0.6" stopColor="#ffe0ab" />
          <stop offset="1" stopColor="#ffbf74" />
        </radialGradient>
        <radialGradient id="gw-halo">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.8" />
          <stop offset="1" stopColor={C.warmHot} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="gw-cloud" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={cloudShade} />
          <stop offset="0.55" stopColor={cloudShade} />
          <stop offset="1" stopColor={cloudLit} />
        </linearGradient>
        <radialGradient id="gw-puff" cx="0.4" cy="0.6">
          <stop offset="0" stopColor={mixColor("#2e2a2a", "#e9b88a", dawn * 0.75)} stopOpacity="0.8" />
          <stop offset="0.55" stopColor={mixColor("#252223", "#9a7a6a", dawn * 0.7)} stopOpacity="0.4" />
          <stop offset="1" stopColor="#1d1b1c" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="gw-steam">
          <stop offset="0" stopColor="#f6efe6" stopOpacity="0.95" />
          <stop offset="0.5" stopColor="#efe4d6" stopOpacity="0.55" />
          <stop offset="1" stopColor="#efe4d6" stopOpacity="0" />
        </radialGradient>
        <pattern id="gw-mesh" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <path d="M 0 0 V 7 M 0 0 H 7" stroke="#3a332d" strokeWidth="0.8" fill="none" />
        </pattern>
        <clipPath id="gw-water">
          <rect x={BLEED.x} y={WATER_Y} width={BLEED.w} height={2400} />
        </clipPath>
        <clipPath id="gw-millwin">
          <path d={MILL_WIN.open} />
        </clipPath>
      </defs>

      {/* Sky */}
      <rect x={BLEED.x - 400} y={-1500} width={BLEED.w + 800} height={1500 + GY + 2} fill="url(#gw-sky)" />
      {STARS.map((s, i) => {
        const a = s.a * starFade(s) * (0.72 + 0.28 * Math.sin(local * s.rate + s.ph));
        return a > 0.02 ? <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#dfe6f2" opacity={a} /> : null;
      })}
      {glow > 0 && <ellipse cx={SUN_X} cy={glowY} rx={1050} ry={440} fill="url(#gw-glow)" opacity={glow} />}
      <g fill="url(#gw-cloud)">
        {CLOUD_D.map((d, i) => {
          const [x, , w, , v] = CLOUDS[i];
          const near = 1 - clamp(Math.abs(x + w / 2 - SUN_X) / 1000);
          return (
            <path key={i} d={d} transform={`translate(${v * (local + 14)} 0)`}
              opacity={(0.2 + 0.6 * seg(dawn, 0.05, 0.4)) * (0.6 + 0.4 * near)} />
          );
        })}
      </g>
      {sunUp > 0 && (
        <g>
          <circle cx={SUN_X} cy={sunY} r={SUN_R * 2.6} fill="url(#gw-halo)" opacity={0.55 * sunUp + 0.2} />
          <circle cx={SUN_X} cy={sunY} r={SUN_R} fill="url(#gw-sun)" />
        </g>
      )}

      {/* Everything that stands on the horizon; the river mirrors this group. */}
      <g id="gw-sil">
        <path d={TREELINE} fill={farInk} />
        <path d={TOWN_D} fill={townInk} />
        <path d={TOWER_D} stroke={townInk} strokeWidth={3} fill="none" />
        <path d={TOWER_BRACE_D} stroke={townInk} strokeWidth={1.4} fill="none" />
        {LIT.map(([h, u, on, off], i) => {
          const [x, w, wall] = HOUSES[h];
          const lit = seg(local, on, on + 0.15) * (1 - seg(local, off, off + 0.15));
          return lit > 0 ? (
            <rect key={i} x={x + w * u - 3} y={GY - wall * 0.66} width={6} height={7} fill={C.warmHot} opacity={0.85 * lit} />
          ) : null;
        })}

        {/* The mill: empty, chained */}
        <path d={MILL_D} fill={townInk} />
        <path d={BELFRY_D} fill={townInk} fillRule="evenodd" />
        <path d="M 477 452 V 444" stroke={townInk} strokeWidth={1.5} />
        <path d={MILL_WIN.shut} fill={mixColor(townInk, low, 0.12)} />
        <g clipPath="url(#gw-millwin)">
          <rect x={MILL.x0} y={480} width={MILL.x1 - MILL.x0} height={GY - 480} fill="url(#gw-sky)" />
          {glow > 0 && <ellipse cx={SUN_X} cy={glowY} rx={1050} ry={440} fill="url(#gw-glow)" opacity={glow * 0.8} />}
        </g>
        <text x={562} y={394} fill={mixColor(townInk, "#d8c8b0", 0.3)} fontFamily={FONT.mono} fontSize={9.5}
          fontWeight={500} textAnchor="middle">
          {"TEXTILE".split("").map((ch, i) => (
            <tspan key={i} x={562} dy={i ? 11.6 : 0}>{ch}</tspan>
          ))}
        </text>
        <rect x={392} y={MILL_FENCE_TOP} width={200} height={GY - MILL_FENCE_TOP} fill="url(#gw-mesh)" />
        <path d={MILL_GATE_D} fill="url(#gw-mesh)" stroke={nearInk} strokeWidth={1.6} />
        <path d={MILL_FENCE_D} stroke={nearInk} strokeWidth={2.2} fill="none" />
        <path d={`M ${MILL_GATE.l} ${GY + 1} V ${MILL_FENCE_TOP - 12} M ${MILL_GATE.r} ${GY + 1} V ${MILL_FENCE_TOP - 12}`}
          stroke={nearInk} strokeWidth={3.5} />
        <Shut x={520} y={MILL_FENCE_TOP + 6} ink={nearInk} plate={plate} s={0.8} />

        {/* Plant */}
        <path d={STACK_D} fill={plantInk} />
        {STACKS.map(({ x, top: ty, w1, ph }, i) => (
          <g key={i}>
            <rect x={x - w1 / 2 - 0.5} y={ty + 9} width={w1 + 1} height={3} fill={mixColor(low, plantInk, 0.6)} />
            <rect x={x - w1 / 2 - 0.5} y={ty + 17} width={w1 + 1} height={2} fill={mixColor(low, plantInk, 0.7)} />
            {((local * 0.75 + ph) % 1) < 0.16 && (
              <>
                <circle cx={x} cy={ty - 3} r={9} fill="url(#gw-halo)" opacity={0.5} />
                <circle cx={x} cy={ty - 3} r={2.2} fill="#ff6a45" />
              </>
            )}
          </g>
        ))}
        <path d={PLANT_D} fill={plantInk} />
        <path d={SIGN_D} stroke={plantInk} strokeWidth={2.5} fill="none" />
        <text x={SIGN.x} y={SIGN.y} fill={plantInk} fontFamily={FONT.mono} fontSize={23} fontWeight={500}
          letterSpacing="0.08em" textAnchor="middle">PLANT 4</text>
        <text x={SIGN.x} y={SIGN.y + 19} fill={plantInk} fontFamily={FONT.mono} fontSize={10.5} fontWeight={600}
          letterSpacing="0.16em" textAnchor="middle">AUTO BODY</text>
        {GLAZING.map((d, i) => {
          const on = seg(sinceWhistle, i * 0.09, i * 0.09 + 0.25);
          // Half the bays stay dark: the second shift is gone.
          const dark = i % 3 === 2 || i > 9;
          return <path key={i} d={d} fill={C.warm} opacity={dark ? 0.08 : 0.3 + 0.6 * Math.max(on, plantLit - 0.55)} />;
        })}
        <path d={FACADE_WIN_D} fill={C.warm} opacity={0.14 + 0.18 * plantLit} />
        <rect x={DOOR.x} y={DOOR.y} width={DOOR.w} height={42} fill={C.warmHot} opacity={0.88} />
        <path d={`M ${WHISTLE.x} ${WHISTLE.y} v -16 M ${WHISTLE.x - 4} ${WHISTLE.y - 16} h 8`} stroke={plantInk} strokeWidth={4} />

        {/* Through the gate, behind the fence */}
        {inside.map(drawWorker)}
        <rect x={FENCE_X0} y={FENCE_TOP} width={GATE_L - FENCE_X0} height={GY - FENCE_TOP} fill="url(#gw-mesh)" />
        <rect x={GATE_R} y={FENCE_TOP} width={2010 - GATE_R} height={GY - FENCE_TOP} fill="url(#gw-mesh)" />
        <path d={GATE_LEAVES_D} fill="url(#gw-mesh)" stroke={nearInk} strokeWidth={2} />
        <path d={GATE3_D} fill="url(#gw-mesh)" stroke={nearInk} strokeWidth={2} />
        <path d={FENCE_POSTS_D} stroke={nearInk} strokeWidth={2.5} fill="none" />
        <path d={`M ${GATE_L} ${GY + 1} V ${GY - 104} M ${GATE_R} ${GY + 1} V ${GY - 104}`} stroke={nearInk} strokeWidth={5} />
        <path d={`M ${GATE3.l} ${GY + 1} V ${FENCE_TOP - 14} M ${GATE3.r} ${GY + 1} V ${FENCE_TOP - 14}`} stroke={nearInk} strokeWidth={4} />
        <Shut x={(GATE3.l + GATE3.r) / 2} y={FENCE_TOP + 12} ink={nearInk} plate={plate} />
        <rect x={GATE_L - 6} y={GY - 122} width={GATE_R - GATE_L + 12} height={17} fill={nearInk} />
        <Label x={(GATE_L + GATE_R) / 2} y={GY - 110} size={9.5} anchor="middle" color={C.warm} opacity={0.4 + 0.5 * gateLamp} spacing={0.3}>
          GATE 2
        </Label>
        <path d={`M ${GATE_L} ${GY - 122} v -8 h 9`} stroke={nearInk} strokeWidth={2.5} fill="none" />
        {gateLamp > 0 && (
          <>
            <circle cx={GATE_L + 9} cy={GY - 127} r={56} fill="url(#gw-halo)" opacity={0.42 * gateLamp} />
            <circle cx={GATE_L + 9} cy={GY - 127} r={2.6} fill={C.warmHot} opacity={gateLamp} />
          </>
        )}

        {/* Poles, wires, birds */}
        <path d={POLE_D} fill={nearInk} />
        <path d={WIRE_D} stroke={nearInk} strokeWidth={1.3} fill="none" />
        {BIRDS.map((b, i) => {
          const tau = local - (whistleAt + 0.25 + b.delay);
          if (tau < 0) {
            const look = Math.sin(local * b.look * 3 + b.bob) > 0.7 ? 1.2 : 0;
            return (
              <g key={i} transform={`translate(${b.bx} ${b.by}) scale(${b.face} 1)`} fill={nearInk}>
                <ellipse cx={0} cy={-5} rx={4.6} ry={3.6} />
                <circle cx={3.6 + look} cy={-9} r={2.4} />
                <path d="M -3.5 -4 L -8.5 -1.5 L -3 -2.5 Z" />
              </g>
            );
          }
          if (tau > 7) return null;
          const x = b.bx + b.vx * tau + 12 * Math.sin(tau * 2.1 + b.bob);
          const y = b.by - 6 - b.vy * tau - 10 * tau * tau;
          const f = Math.sin(tau * b.flap);
          const w = 7 + 1.5 * tau;
          return (
            <path
              key={i}
              d={`M ${x - w} ${y - 4 * f} Q ${x - w * 0.45} ${y - 1.5 * f - 1} ${x} ${y} Q ${x + w * 0.45} ${y - 1.5 * f - 1} ${x + w} ${y - 4 * f}`}
              stroke={nearInk} strokeWidth={2} fill="none" strokeLinecap="round"
              opacity={1 - seg(tau, 5.5, 7)}
            />
          );
        })}

        {/* On the road */}
        {outside.map(drawWorker)}
        {!firstGo && <circle cx={hand[0]} cy={hand[1]} r={1.7} fill="#ffb070" opacity={ember} />}
        {firstGo && flick < 1 && (
          <circle cx={hand[0] - 12 * flick} cy={lerp(hand[1], GY - 1, flick * flick)} r={1.5} fill="#ffb070" opacity={1 - flick * 0.5} />
        )}
      </g>

      {/* Smoke from the one live stack, and the shift whistle */}
      {SMOKE.map((m, i) => {
        const { x, top: ty } = STACKS[0];
        const age = ((local / SMOKE_LIFE + m.off) % 1) * SMOKE_LIFE;
        const u = age / SMOKE_LIFE;
        const r = (13 + 62 * Math.pow(u, 0.8)) * m.size;
        const op = 0.42 * Math.min(1, u / 0.04) * Math.pow(1 - u, 1.5);
        return (
          <circle
            key={i}
            cx={x + 200 * Math.pow(u, 1.4) + 9 * Math.sin(m.wob + u * 5)}
            cy={ty - 8 - 330 * Math.pow(u, 0.85) * m.rise}
            r={r}
            fill="url(#gw-puff)"
            opacity={op}
          />
        );
      })}
      {sinceWhistle > 0 && sinceWhistle < 5 &&
        STEAM.map((m, i) => {
          const a = sinceWhistle - m.at;
          if (a < 0 || a > 3) return null;
          return (
            <circle
              key={i}
              cx={WHISTLE.x + m.dx * a * 2 + 16 * a}
              cy={WHISTLE.y - 16 - 110 * (1 - Math.exp(-2.2 * a))}
              r={(4 + 15 * Math.sqrt(a)) * m.size}
              fill="url(#gw-steam)"
              opacity={0.5 * Math.pow(1 - a / 3, 1.6)}
            />
          );
        })}

      {/* The river: mirrored sky, town and walkers, broken by ripples */}
      <rect x={BLEED.x - 400} y={WATER_Y} width={BLEED.w + 800} height={2400} fill="url(#gw-sky-r)" />
      <g clipPath="url(#gw-water)">
        {glow > 0 && <ellipse cx={SUN_X} cy={2 * MIRROR - glowY} rx={1050} ry={440} fill="url(#gw-glow)" opacity={glow * 0.85} />}
        {sunUp > 0 && <ellipse cx={SUN_X} cy={2 * MIRROR - sunY} rx={SUN_R} ry={SUN_R * 0.8} fill="url(#gw-sun)" opacity={0.42} />}
        <use href="#gw-sil" transform={`matrix(1 0 0 -1 0 ${2 * MIRROR})`} opacity={0.85} />
      </g>
      <rect x={BLEED.x - 400} y={WATER_Y} width={BLEED.w + 800} height={2400} fill="url(#gw-deep)" />
      {RIPPLES.map((rw, i) => (
        <path key={i} d={`M ${BLEED.x - 400} ${rw.y} H ${BLEED.x + BLEED.w + 400}`} stroke="#000000" strokeOpacity={rw.op}
          strokeWidth={rw.w} strokeDasharray={rw.dash} strokeDashoffset={rw.v * local} />
      ))}
      {sunUp > 0 && GLINTS.map((g, i) => {
        const tw = Math.pow(0.5 + 0.5 * Math.sin(local * g.rate + g.ph), 2);
        return (
          <rect key={i} x={SUN_X + g.dx - g.w / 2} y={g.y} width={g.w} height={1.6} fill={C.warmHot}
            opacity={sunUp * (0.25 + 0.6 * tw) * (1 - i / 30)} />
        );
      })}
      <rect x={BLEED.x - 400} y={GY + 1.5} width={BLEED.w + 800} height={WATER_Y - GY - 1.5} fill={BANK} />
      <path d={`M ${BLEED.x - 400} ${GY + 0.5} H ${BLEED.x + BLEED.w + 400}`} stroke={mixColor(hor, "#000000", 0.75)} strokeWidth={1.5} />
      <path d={REEDS_D} stroke="#020202" strokeWidth={2.4} fill="none" strokeLinecap="round" />
      {CATTAILS.map(([x, y]) => (
        <rect key={x} x={x - 3} y={y} width={6} height={22} rx={3} fill="#020202" />
      ))}
    </g>
  );
}

// ===========================================================================
// 2 · Thirty years on Line B
// ===========================================================================

const MT0 = 0.285; // the montage starts (p)
const Y0P = 0.335; // first card stamped 1995
const Y1P = 0.66; // last card, 2025
const OUT_P = 0.676; // stamped OUT
// Slow at either end so the first and last years read; quick in the middle.
const yearAt = (p) => {
  const k = seg(p, Y0P, Y1P);
  return 1995 + 30 * (0.42 * k + 0.58 * smooth(k));
};

const FL = 700; // shop floor
const HI = 96; // top of the roof glass
const LO = 224; // foot of the roof glass
const CHORD = 262; // truss bottom chord
const TW = 330; // sawtooth bay
const GW = 60; // roof glass as seen just off edge-on
const BAYS = Array.from({ length: 8 }, (_, i) => -380 + i * TW);
const slopeY = (x) => {
  const b = Math.floor((x + 380) / TW);
  const xb = -380 + b * TW;
  return HI + (LO - HI) * ((x - xb) / TW);
};
const CEIL_D = (() => {
  let d = `M 2040 -1000 H -440 V ${slopeY(-440)}`;
  BAYS.forEach((xb) => {
    d += ` L ${xb} ${LO} L ${xb} ${HI}`;
    d += ` L ${xb + TW} ${LO}`;
  });
  return d + ` L 2040 ${slopeY(2040)} Z`;
})();
const GLASS_D = BAYS.map((xb) =>
  `M ${xb} ${HI} L ${xb + GW} ${slopeY(xb + GW)} L ${xb + GW} ${LO} L ${xb} ${LO} Z`
).join(" ");
const MULLION_D = BAYS.map((xb) => {
  const y1 = HI + (LO - HI) * 0.36;
  const y2 = HI + (LO - HI) * 0.68;
  return `M ${xb} ${y1} H ${xb + GW} M ${xb} ${y2} H ${xb + GW} M ${xb + GW / 2} ${slopeY(xb + GW / 2)} V ${LO}`;
}).join(" ");
// Trusses: bottom chord, posts and diagonals up to the roof slope.
const TRUSS_D = (() => {
  const d = [`M -440 ${CHORD} H 2040`];
  for (let x = -440; x < 2040; x += 55) {
    const xb = -380 + Math.floor((x + 380) / TW) * TW;
    if (x - xb < GW + 8) continue;
    d.push(`M ${x} ${CHORD} V ${slopeY(x)}`);
    d.push(`M ${x} ${CHORD} L ${x + 27} ${slopeY(x + 27)}`);
  }
  return d.join(" ");
})();
const COLUMNS = [-140, 220, 640, 1060, 1480, 1900];
const COLUMN_D = COLUMNS.map((x) =>
  `M ${x - 9} ${FL} V ${CHORD} H ${x + 9} V ${FL} Z`
).join(" ");
const FLANGE_D = COLUMNS.map((x) => `M ${x - 4} ${FL} V ${CHORD} M ${x + 4} ${FL} V ${CHORD}`).join(" ");

// The overhead line: bare car bodies (no wheels yet) on carriers.
const RAIL_Y = 300;
const PITCH = 420;
const CARRIERS = 7;
const LOOP = PITCH * CARRIERS;
const BODY_D =
  "M -100 34 L -97 20 Q -95 13 -82 12 L -42 9 L -20 -8 Q -16 -11 -10 -11 L 34 -11 Q 40 -11 44 -7 L 62 7 L 92 10 " +
  "Q 99 11 100 18 L 100 34 L 74 34 A 17 17 0 0 0 40 34 L -46 34 A 17 17 0 0 0 -80 34 Z " +
  "M -14 -4 L -32 9 L 2 9 L 2 -4 Z M 8 -4 L 8 9 L 46 9 L 34 -4 Z";
const CARRIER_D = "M -64 0 V 46 H 66 V 0 M -70 46 H -50 M 50 46 H 72";
const lineTravel = (tau, ts, te) => {
  const v0 = 120;
  const v1 = 300;
  const T1 = Math.min(9, ts);
  if (tau <= 0) return v0 * tau;
  if (tau <= T1) return v0 * tau + ((v1 - v0) * tau * tau) / (2 * T1);
  const x1 = ((v0 + v1) * T1) / 2;
  if (tau <= ts) return x1 + v1 * (tau - T1);
  const xs = x1 + v1 * (ts - T1);
  if (tau <= te) return xs + v1 * (tau - ts) - (v1 * (tau - ts) * (tau - ts)) / (2 * (te - ts));
  return xs + (v1 * (te - ts)) / 2;
};

// Stations along the line, and the year each one goes dark.
const MS = 1.45;
const BENCH_TOP = FL - 96 * MS + 2;
const LAMP_Y = 424;
const STATIONS = [
  { x: -60, off: 2004, ph: 2.1 },
  { x: 380, him: true },
  { x: 820, off: 2017, ph: 0.7 },
  { x: 1260, off: 2010, ph: 4.0 },
  { x: 1700, off: 1999, ph: 1.3 },
];
const benchD = (x) => {
  const x0 = x + 34 * MS;
  const x1 = x + 236 * MS;
  return (
    `M ${x0} ${BENCH_TOP} H ${x1} V ${BENCH_TOP + 10} H ${x0} Z ` +
    `M ${x0 + 8} ${BENCH_TOP + 10} V ${FL} h 7 V ${BENCH_TOP + 10} Z M ${x1 - 15} ${BENCH_TOP + 10} V ${FL} h 7 V ${BENCH_TOP + 10} Z ` +
    `M ${x0 + 8} ${FL - 40} H ${x1 - 8} v 5 H ${x0 + 8} Z`
  );
};
const lampX = (x) => x + 150 * MS;

// Seasons and days through the roof glass.
const DAY = 2.6;
const SEASONS = 7.6;
// Daylight through the glass stays warm-grey: the first cold light in the
// piece belongs to the terminal at the end of the act.
const SEASON_SKY = [[0, "#b3b8b0"], [0.25, "#d7a46d"], [0.5, "#cbc8c2"], [0.75, "#b9c2a2"], [1, "#b3b8b0"]];
const FLAKES = (() => {
  const r = rng(5150);
  return Array.from({ length: 34 }, () => ({
    bay: 1 + Math.floor(r() * 6), u: r(), v: 0.55 + r() * 0.7, y0: r(), sway: r() * 6.28, size: 0.9 + r() * 1.3,
  }));
})();
const LEAVES = (() => {
  const r = rng(6262);
  return Array.from({ length: 12 }, () => ({
    bay: 1 + Math.floor(r() * 6), y0: r(), v: 0.7 + r() * 0.8, spin: r() * 6.28, x0: r(),
  }));
})();

// The time clock insert, in its own 380 x 292 box.
const INS = { x: 1040, y: 112, w: 380, h: 292 };
const SLOT = 198;
const CARD = { x: 148, y: 52, w: 98 };
const TICKS_D = (() => {
  const d = [];
  for (let i = 0; i < 60; i++) {
    const a = (i / 60) * Math.PI * 2;
    const r0 = i % 5 === 0 ? 30 : 34;
    d.push(`M ${Math.sin(a) * r0} ${-Math.cos(a) * r0} L ${Math.sin(a) * 37} ${-Math.cos(a) * 37}`);
  }
  return d.join(" ");
})();

function TimeCard({ year, stamp, outK }) {
  return (
    <g>
      <rect width={CARD.w} height={200} fill="url(#gw-paper)" stroke="#b9a37f" strokeWidth={0.8} />
      <text x={7} y={16} fill="#2a221b" fontFamily={FONT.mono} fontSize={11} fontWeight={500} letterSpacing="0.05em">NO. 0417</text>
      <text x={7} y={26} fill="#5d4f40" fontFamily={FONT.mono} fontSize={5.6} letterSpacing="0.14em">PLANT 4 · LINE B</text>
      <path d={`M 5 31 H ${CARD.w - 5} M 5 84 H ${CARD.w - 5} M 5 100 H ${CARD.w - 5} M 5 116 H ${CARD.w - 5} M 5 132 H ${CARD.w - 5}`}
        stroke="#b7a383" strokeWidth={0.7} />
      <text x={7} y={95} fill="#4f3d66" opacity={0.65} fontFamily={FONT.mono} fontSize={7}>IN 05:58</text>
      {stamp > 0 && (
        <g transform={`rotate(-3 ${CARD.w / 2} 62)`} opacity={stamp}>
          <text x={CARD.w / 2 + 0.6} y={70.6} fill="#9b2c1c" opacity={0.35} fontFamily={FONT.mono} fontSize={30}
            fontWeight={600} textAnchor="middle" letterSpacing="0.02em">{year}</text>
          <text x={CARD.w / 2} y={70} fill="#9b2c1c" opacity={0.9} fontFamily={FONT.mono} fontSize={30}
            fontWeight={600} textAnchor="middle" letterSpacing="0.02em">{year}</text>
        </g>
      )}
      {outK > 0 && (
        <text x={7} y={111} fill="#9b2c1c" opacity={outK * 0.9} fontFamily={FONT.mono} fontSize={9} fontWeight={600}
          transform="rotate(-1.5 40 108)">OUT 14:31</text>
      )}
    </g>
  );
}

function ClockInsert({ k, p, local, dur, Y, tau }) {
  const n = Math.min(30, Math.floor(Y - 1995 + 1e-6));
  // f runs 0 -> 1 through each card's year; the last card gets its own beat.
  const f = n >= 30 ? seg(p, Y1P, Y1P + 0.012) : frac(Y - 1995);
  // Card n-1 flips away to the pile and card n flips in; then the head stamps
  // it. The first card has just dropped in, so it is only stamped.
  const u = n === 0 ? 1 : seg(f, 0, 0.16);
  const flipS = Math.abs(Math.cos(Math.PI * u));
  const shown = u < 0.5 ? n - 1 : n;
  const firstPeak = Y0P - 0.003;
  const stamped = n === 0 ? (p >= firstPeak ? 1 : 0) : u < 0.5 ? 1 : f >= 0.2 ? 1 : 0;
  const press = n === 0 ? span1(seg(p, firstPeak - 0.009, firstPeak + 0.009)) : span1(seg(f, 0.12, 0.3));
  const sinceOut = local - OUT_P * dur;
  const outPress = sinceOut > -0.14 && sinceOut < 0.5 ? span1(clamp((sinceOut + 0.14) / 0.64)) : 0;
  const head = Math.max(press, outPress);
  const pile = Math.max(0, u < 0.5 ? n - 1 : n);
  const drop = ease.out(seg(p, 0.3, 0.326));
  const cardY = lerp(-170, CARD.y, drop);
  const headY = lerp(26, 132, head);
  const day = frac(tau / DAY);
  const lampFlicker = 0.94 + 0.06 * Math.sin(local * 13.7) * Math.sin(local * 3.1);
  const pileTop = SLOT - 2 - pile * 2.1;

  return (
    <g opacity={k} transform={`translate(${INS.x} ${INS.y})`}>
      <defs>
        <clipPath id="gw-ins">
          <rect width={INS.w} height={INS.h} />
        </clipPath>
        <clipPath id="gw-slot">
          <rect width={INS.w} height={SLOT} />
        </clipPath>
      </defs>
      <g clipPath="url(#gw-ins)">
        <rect width={INS.w} height={INS.h} fill="#17110c" />
        <rect width={INS.w} height={INS.h} fill="url(#gw-block)" />
        <ellipse cx={196} cy={-10} rx={300} ry={250} fill="url(#gw-lamp)" opacity={lampFlicker} />

        {/* Wall clock: days spin by */}
        <g transform="translate(66 80)">
          <circle r={46} fill="#0f0b08" />
          <circle r={42} fill="#2a2119" />
          <circle r={38} fill="url(#gw-dial)" />
          <path d={TICKS_D} stroke="#2a221b" strokeWidth={1} fill="none" />
          <path d="M 0 4 L 0 -20" stroke="#16110d" strokeWidth={3.4} strokeLinecap="round" transform={`rotate(${day * 720})`} />
          <path d="M 0 5 L 0 -31" stroke="#16110d" strokeWidth={2.2} strokeLinecap="round" transform={`rotate(${day * 360 * 6})`} />
          <circle r={2.4} fill="#16110d" />
        </g>

        {/* The press, the card in its slot, the pile of years */}
        <path d={`M 142 ${SLOT} V 20 M 250 ${SLOT} V 20`} stroke="#5a4a3b" strokeWidth={4} />
        <rect x={134} y={12} width={124} height={10} rx={2} fill="#241d17" stroke="#6a5644" strokeWidth={0.8} />
        <g clipPath="url(#gw-slot)">
          {drop > 0 && (
            <g transform={`translate(${CARD.x} ${cardY}) translate(0 ${70}) scale(1 ${Math.max(0.02, flipS)}) translate(0 ${-70})`}>
              <TimeCard year={shown >= 0 ? 1995 + shown : 1995} stamp={shown >= 0 ? stamped : 0}
                outK={n >= 30 && sinceOut >= 0 ? 1 : 0} />
              {flipS < 1 && <rect width={CARD.w} height={200} fill="#000" opacity={(1 - flipS) * 0.45} />}
            </g>
          )}
        </g>
        <g transform={`translate(0 ${headY})`}>
          <rect x={136} y={0} width={120} height={16} rx={2} fill="#241c16" stroke="#6a5644" strokeWidth={0.8} />
          <rect x={150} y={14} width={92} height={4} fill="#0d0a08" />
          <path d="M 138 1 H 254" stroke={C.warmHot} strokeOpacity={0.35} strokeWidth={0.8} />
        </g>
        <path d={`M 104 ${INS.h + 4} V ${SLOT + 10} Q 104 ${SLOT} 114 ${SLOT} H 278 Q 288 ${SLOT} 288 ${SLOT + 10} V ${INS.h + 4} Z`}
          fill="url(#gw-steel)" />
        <rect x={132} y={SLOT - 4} width={128} height={8} rx={1.5} fill="#2c241d" stroke="#5c4a39" strokeWidth={0.8} />
        <rect x={140} y={SLOT - 1.5} width={112} height={2.5} fill="#050403" />
        <rect x={150} y={232} width={92} height={20} rx={2} fill="#15110d" stroke="#5c4a39" strokeWidth={0.8} />
        <text x={196} y={245.5} fill="#c9b28e" fontFamily={FONT.mono} fontSize={7.5} letterSpacing="0.16em" textAnchor="middle">
          PLANT 4 · GATE 2
        </text>
        <rect x={218} y={262} width={28} height={14} fill="#0b0806" stroke="#5c4a39" strokeWidth={0.8} />
        <text x={232} y={272} fill={n >= 30 && sinceOut >= 0 ? "#e0573c" : C.warm} fontFamily={FONT.mono} fontSize={7}
          letterSpacing="0.08em" textAnchor="middle">{n >= 30 && sinceOut >= 0 ? "OUT" : "IN"}</text>

        {/* Pile */}
        <rect x={296} y={SLOT + 2} width={76} height={6} fill="#2b2018" stroke="#46372a" strokeWidth={0.8} />
        {pile > 0 && (
          <>
            <rect x={300} y={pileTop} width={68} height={SLOT + 2 - pileTop} fill="url(#gw-edges)" />
            <path d={`M 300 ${pileTop} L 368 ${pileTop} L 362 ${pileTop - 9} L 306 ${pileTop - 9} Z`} fill="#e6d7b8" />
            <text x={334} y={pileTop - 2.5} fill="#9b2c1c" opacity={0.8} fontFamily={FONT.mono} fontSize={6} fontWeight={600}
              textAnchor="middle">{1995 + pile - 1}</text>
          </>
        )}
      </g>
      <rect width={INS.w} height={INS.h} fill="none" stroke={C.ink[6]} strokeOpacity={0.55} strokeWidth={1.2} />
      <path d={`M -8 18 V -8 H 18 M ${INS.w + 8} ${INS.h - 18} V ${INS.h + 8} H ${INS.w - 18}`}
        stroke={C.ink[6]} strokeOpacity={0.7} strokeWidth={1.2} fill="none" />
      <Label x={0} y={-16} size={10} color={C.ink[6]}>TIME CLOCK · GATE 2</Label>
      <Label x={INS.w} y={INS.h + 24} size={10} anchor="end" color={C.ink[5]}>NO. 0417</Label>
    </g>
  );
}

// 0 -> 1 -> 0 over k in [0, 1].
function span1(k) {
  return Math.sin(Math.PI * clamp(k));
}

// Phase that slows as the years go on: the hammer rate falls to 55%.
const hammerPhase = (tau, total) => {
  const t = Math.max(0, tau);
  const T = Math.min(t, total);
  return 7 * (T - (0.45 * T * T) / (2 * total) + 0.55 * Math.max(0, t - total));
};

function Montage({ p, local, dur, k, zoom }) {
  const tau = local - MT0 * dur;
  const Y = yearAt(p);
  const total = (Y1P - MT0) * dur;

  // Days and seasons.
  const d = frac((tau + 0.18 * DAY) / DAY); // 0 sunrise, .5 sunset
  const alt = Math.sin(d * Math.PI * 2);
  const light = smooth(clamp(alt * 2.2 + 0.35));
  const twilight = clamp(1 - Math.abs(alt) * 3.2);
  const q = frac(tau / SEASONS);
  const winter = smooth(clamp(1 - Math.abs(q - 0.5) / 0.17));
  const autumn = smooth(clamp(1 - Math.abs(q - 0.27) / 0.12));
  const daySky = keyed(SEASON_SKY, q, 1);
  const glass = mixColor(mixColor("#0d0f16", daySky, light), "#e58d55", twilight * 0.55);
  const theta = lerp(1.05, 0.12, clamp(d / 0.5));
  const shaftK = light * (d < 0.5 ? 1 : 0) * 0.9;
  const shaftCol = mixColor("#fff1d8", "#ffb36a", clamp(1 - Math.abs(d - 0.25) * 5));
  const wallTop = mixColor("#0b0807", "#2a231c", light * 0.9);
  const wallBot = mixColor("#0f0b09", "#3b3127", light * 0.9);
  const floorC = mixColor("#060504", "#1a1612", light);
  const sil = "#070504";

  // The line runs faster and faster, then stops for good.
  const travel = lineTravel(tau, (0.615 - MT0) * dur, (Y1P - MT0) * dur);
  const fewer = lerp(-0.1, 0.8, seg(Y, 2000, 2024));

  // He ages a step at each five-year beat, and his hammer slows.
  const a6 = clamp((Y - 1995) / 30) * 6;
  const age = Math.min(1, (Math.floor(a6) + smooth(seg(frac(a6), 0, 0.3))) / 6);
  const rest = smooth(seg(p, 0.664, 0.69));
  const phase = hammerPhase(tau, total);
  const P = mixPose(workPose(phase, age), REST, rest);

  const insK = smooth(seg(p, 0.298, 0.322)) * (1 - smooth(seg(p, 0.684, 0.7)));
  const cx = 600;
  const cy = 560;
  // Framed a touch low so the polished floor carries the lamps' reflections.
  const camera = `translate(${cx} ${cy}) scale(${zoom}) translate(${-cx} ${-cy}) translate(800 96) scale(1.07) translate(-800 -96)`;

  return (
    <g opacity={k}>
      <g transform={camera}>
        <defs>
          <linearGradient id="gw-floor" gradientUnits="userSpaceOnUse" x1="0" y1={FL} x2="0" y2={FL + 220}>
            <stop offset="0" stopColor={mixColor(floorC, "#2a2219", 0.5)} />
            <stop offset="1" stopColor={floorC} />
          </linearGradient>
          <linearGradient id="gw-floorfade" gradientUnits="userSpaceOnUse" x1="0" y1={FL} x2="0" y2={FL + 200}>
            <stop offset="0" stopColor="#000000" stopOpacity="0" />
            <stop offset="1" stopColor="#000000" stopOpacity="0.85" />
          </linearGradient>
          <clipPath id="gw-floorclip">
            <rect x={-440} y={FL} width={2480} height={1200} />
          </clipPath>
          <linearGradient id="gw-wall" gradientUnits="userSpaceOnUse" x1="0" y1={LO} x2="0" y2={FL}>
            <stop offset="0" stopColor={wallTop} />
            <stop offset="1" stopColor={wallBot} />
          </linearGradient>
          <linearGradient id="gw-shaft" gradientUnits="userSpaceOnUse" x1="0" y1={HI} x2="0" y2={FL}>
            <stop offset="0" stopColor={shaftCol} stopOpacity="0.3" />
            <stop offset="0.7" stopColor={shaftCol} stopOpacity="0.1" />
            <stop offset="1" stopColor={shaftCol} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="gw-cone" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={C.warmHot} stopOpacity="0.5" />
            <stop offset="1" stopColor={C.warm} stopOpacity="0.04" />
          </linearGradient>
          <radialGradient id="gw-pool">
            <stop offset="0" stopColor={C.warm} stopOpacity="0.35" />
            <stop offset="1" stopColor={C.warm} stopOpacity="0" />
          </radialGradient>
          <clipPath id="gw-glass">
            <path d={GLASS_D} />
          </clipPath>
          <pattern id="gw-block" width="34" height="17" patternUnits="userSpaceOnUse">
            <path d="M 0 0.5 H 34 M 0.5 0 V 8.5 M 17.5 8.5 V 17 M 0 8.5 H 34" stroke="#000" strokeOpacity="0.35" strokeWidth="0.8" fill="none" />
          </pattern>
          <pattern id="gw-edges" width="4" height="2.1" patternUnits="userSpaceOnUse">
            <rect width="4" height="2.1" fill="#d8c6a2" />
            <rect y="1.5" width="4" height="0.6" fill="#8d7a60" />
          </pattern>
          <radialGradient id="gw-lamp">
            <stop offset="0" stopColor={C.warmHot} stopOpacity="0.5" />
            <stop offset="0.35" stopColor={C.warm} stopOpacity="0.2" />
            <stop offset="1" stopColor={C.warm} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="gw-steel" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#4d4135" />
            <stop offset="0.4" stopColor="#2d261f" />
            <stop offset="1" stopColor="#15110d" />
          </linearGradient>
          <radialGradient id="gw-dial" cx="0.42" cy="0.38">
            <stop offset="0" stopColor="#f6ecd8" />
            <stop offset="1" stopColor="#cdb994" />
          </radialGradient>
          <linearGradient id="gw-paper" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f3e7cd" />
            <stop offset="1" stopColor="#d8c6a2" />
          </linearGradient>
        </defs>

        {/* Hall */}
        <rect x={BLEED.x - 300} y={BLEED.y} width={BLEED.w + 600} height={BLEED.h} fill={wallTop} />
        <rect x={BLEED.x - 300} y={LO} width={BLEED.w + 600} height={FL - LO} fill="url(#gw-wall)" />
        <path d={GLASS_D} fill={glass} />
        <g clipPath="url(#gw-glass)">
          {winter > 0.02 && FLAKES.map((fl, i) => {
            const xb = BAYS[fl.bay];
            const y = HI + frac(fl.y0 + tau * 0.42 * fl.v) * (LO - HI);
            return <circle key={i} cx={xb + 4 + fl.u * (GW - 8) + 3 * Math.sin(tau * 2 + fl.sway)} cy={y} r={fl.size}
              fill="#f4f6f8" opacity={winter * 0.85} />;
          })}
          {autumn > 0.02 && LEAVES.map((lf, i) => {
            const xb = BAYS[lf.bay];
            const x = xb - 10 + frac(lf.x0 + tau * 0.9 * lf.v) * (GW + 20);
            const y = HI + 10 + lf.y0 * (LO - HI - 20) + 8 * Math.sin(tau * 3 + lf.spin);
            return <ellipse key={i} cx={x} cy={y} rx={3.2} ry={1.6} fill="#5a2c12"
              transform={`rotate(${deg(tau * 4 + lf.spin)} ${x} ${y})`} opacity={autumn} />;
          })}
          {winter > 0.02 && (
            <path d={BAYS.map((xb) => `M ${xb} ${LO} V ${LO - 16 * winter} Q ${xb + GW / 2} ${LO - 22 * winter} ${xb + GW} ${LO - 15 * winter} V ${LO} Z`).join(" ")}
              fill="#e9eef2" opacity={0.9} />
          )}
        </g>
        <path d={MULLION_D} stroke={sil} strokeWidth={3} fill="none" />
        {shaftK > 0.01 && (
          <path
            d={BAYS.map((xb) => {
              // The pane swept along the sun's direction down to the floor.
              const t = Math.tan(theta);
              const sy = slopeY(xb + GW);
              return `M ${xb} ${HI} L ${xb + GW} ${sy} L ${xb + GW + (FL - sy) * t} ${FL} L ${xb + (FL - LO) * t} ${FL} L ${xb} ${LO} Z`;
            }).join(" ")}
            fill="url(#gw-shaft)" opacity={shaftK}
          />
        )}
        <path d={CEIL_D} fill={sil} />
        <path d={TRUSS_D} stroke={sil} strokeWidth={4} fill="none" />
        <path d={`M -440 ${CHORD} H 2040`} stroke={sil} strokeWidth={9} />

        {/* Overhead line */}
        <path d={`M -440 ${RAIL_Y} H 2040`} stroke={sil} strokeWidth={5} />
        <path d={COLUMNS.map((x) => `M ${x - 120} ${CHORD} L ${x - 120} ${RAIL_Y} M ${x + 120} ${CHORD} L ${x + 120} ${RAIL_Y}`).join(" ")}
          stroke={sil} strokeWidth={2} />
        {Array.from({ length: CARRIERS }, (_, i) => {
          const pos = i * PITCH + travel;
          const x = (((pos % LOOP) + LOOP) % LOOP) - 640;
          const lap = Math.floor(pos / LOOP);
          const r = rng(i * 131 + lap * 17 + 5)();
          const has = r > fewer;
          return (
            <g key={i} transform={`translate(${x} ${RAIL_Y})`} fill={sil}>
              <path d={CARRIER_D} stroke={sil} strokeWidth={2.4} fill="none" />
              {has && <path d={BODY_D} transform="translate(0 6)" fillRule="evenodd" />}
            </g>
          );
        })}

        {/* Columns */}
        <path d={COLUMN_D} fill={mixColor(sil, wallBot, 0.18)} />
        <path d={FLANGE_D} stroke={sil} strokeWidth={1.5} />

        {/* Stations */}
        <g id="gw-line">
          {STATIONS.map((st, i) => {
            const on = st.him ? 1 : Y < st.off ? 1 : Y < st.off + 0.5 ? (Math.sin(local * 47 + i) > 0 ? 0.7 : 0.15) : 0;
            const man = st.him ? 1 : 1 - smooth(seg(Y, st.off - 0.2, st.off + 0.4));
            const lx = lampX(st.x);
            const glowK = on * (0.55 + 0.45 * (1 - light));
            return (
              <g key={i}>
                {glowK > 0 && (
                  <>
                    <ellipse cx={lx} cy={BENCH_TOP - 40} rx={260} ry={190} fill="url(#gw-pool)" opacity={glowK} />
                    <path d={`M ${lx - 22} ${LAMP_Y + 14} L ${lx + 22} ${LAMP_Y + 14} L ${lx + 150} ${BENCH_TOP} L ${lx - 150} ${BENCH_TOP} Z`}
                      fill="url(#gw-cone)" opacity={glowK * 0.8} />
                  </>
                )}
                <path d={`M ${lx} ${CHORD} V ${LAMP_Y}`} stroke={sil} strokeWidth={1.5} />
                <path d={`M ${lx - 24} ${LAMP_Y + 15} Q ${lx} ${LAMP_Y - 8} ${lx + 24} ${LAMP_Y + 15} Z`} fill={sil} />
                {on > 0 && <ellipse cx={lx} cy={LAMP_Y + 15} rx={14} ry={3.2} fill={C.warmHot} opacity={on} />}
                <path d={benchD(st.x)} fill={sil} />
                {st.him ? (
                  <>
                    <path d={`M ${st.x + 76} ${FL} v -15 h 22 v 15 Z M ${st.x + 79} ${FL - 15} q 8 -12 16 0`}
                      fill={sil} stroke={sil} strokeWidth={2} />
                    <rect x={st.x + 54 * MS} y={BENCH_TOP - 7} width={46} height={7} fill={sil} />
                    {rest > 0 && (
                      <path d={`M ${st.x + 150 * MS} ${BENCH_TOP - 3} h 34 M ${st.x + 150 * MS + 30} ${BENCH_TOP - 8} v 7`}
                        stroke={sil} strokeWidth={4} opacity={rest} />
                    )}
                    <Worker x={st.x} y={FL} s={MS} color={sil} pose={P} hammer={1 - rest} />
                  </>
                ) : man > 0.01 ? (
                  <Worker x={st.x} y={FL} s={MS} color={sil} pose="work" phase={local * 6.3 + st.ph}
                    hat={i % 2 ? "hard" : "cap"} opacity={man} />
                ) : null}
              </g>
            );
          })}
        </g>

        {/* Polished floor: the line again, upside down and dim */}
        <rect x={BLEED.x - 300} y={FL} width={BLEED.w + 600} height={BLEED.h} fill="url(#gw-floor)" />
        <g clipPath="url(#gw-floorclip)">
          <use href="#gw-line" transform={`matrix(1 0 0 -1 0 ${2 * FL})`} opacity={0.2} />
        </g>
        <rect x={BLEED.x - 300} y={FL} width={BLEED.w + 600} height={BLEED.h} fill="url(#gw-floorfade)" />
        <path d={`M -440 ${FL + 0.5} H 2040`} stroke={mixColor(floorC, "#ffffff", 0.08)} strokeWidth={1.2} />
      </g>
      {insK > 0 && <ClockInsert k={insK} p={p} local={local} dur={dur} Y={Y} tau={tau} />}
    </g>
  );
}

// ===========================================================================
// 3 · His bench
// ===========================================================================

const SB = {
  floor: 856, // his feet
  x0: 600, x1: 1780, back: 582, front: 604, face: 648, // the bench
  bx0: 668, bx1: 1500, btop: 232, // the shadow board
  lampX: 930, lampY: 192,
  man: 452, s: 2.7,
};
const BOX = { x: 770, w: 280, base: 548, lid: 368 };
const WATCH = { x: 910, y: 497, r: 46 };
const TERM = { x: 530, y: 448 }; // the screen's centre: where his head was
// Keys on the console's slope, in rows that narrow toward the screen.
const TERM_KEYS_D = (() => {
  const d = [];
  for (let row = 0; row < 3; row++) {
    const y = 607 - row * 9.5;
    const half = 66 + row * 7;
    for (let x = -half; x < half - 6; x += 12) d.push(`M ${TERM.x + x + 2} ${y} h 7`);
  }
  return d.join(" ");
})();
const REACH = { ...REST, head: 0.62, arms: [[0.1, 0.06], [0.84, 1.2]] };
const DIAL_TICKS_D = (() => {
  const d = [];
  for (let i = 0; i < 60; i++) {
    const a = (i / 60) * Math.PI * 2;
    const r0 = i % 5 === 0 ? (i % 15 === 0 ? 27 : 30) : 34.5;
    d.push(`M ${(Math.sin(a) * r0).toFixed(2)} ${(-Math.cos(a) * r0).toFixed(2)} L ${(Math.sin(a) * 36.5).toFixed(2)} ${(-Math.cos(a) * 36.5).toFixed(2)}`);
  }
  return d.join(" ");
})();
const LINKS_D = (() => {
  const d = [];
  for (let y = WATCH.y - WATCH.r - 26; y < WATCH.y - WATCH.r + 4; y += 4.5) d.push(`M ${WATCH.x - 15} ${y} H ${WATCH.x + 15}`);
  for (let y = WATCH.y + WATCH.r - 4; y < BOX.base + 6; y += 4.5) d.push(`M ${WATCH.x - 15} ${y} H ${WATCH.x + 15}`);
  return d.join(" ");
})();
// Painted outlines on the shadow board and the tools hung in them. The
// hammer's outline is empty: the hammer is on the bench, put down for good.
const TOOLS = [
  { x: 716, y: 268, d: "M -9 0 Q -18 -6 -16 -20 L -8 -14 L 8 -14 L 16 -20 Q 18 -6 9 0 L 7 150 Q 18 158 14 172 L 6 166 L -6 166 L -14 172 Q -18 158 -7 150 Z" },
  { x: 1086, y: 272, d: "M 0 0 H 13 V 158 H 108 V 171 H 0 Z" },
  { x: 1272, y: 282, d: "M -40 -14 H 32 Q 44 -14 44 -2 H 16 V 5 H 7 V 160 Q 7 168 0 168 Q -7 168 -7 160 V 5 H -40 Z", empty: true },
  { x: 1376, y: 284, d: "M -6 0 L -4 -30 Q 0 -36 4 -30 L 6 0 L 18 140 Q 18 148 12 146 L 2 30 L -2 30 L -12 146 Q -18 148 -18 140 Z" },
  { x: 1456, y: 284, d: "M -9 0 H 9 V 60 L 4 66 V 150 L 0 156 L -4 150 V 66 L -9 60 Z" },
];
// The lamp stutters: on/off per 50ms step. He goes in one of the dark steps.
const STUTTER = [1, 0, 1, 1, 0, 0, 1, 0, 0, 0, 1, 1, 0, 1, 1, 1, 0, 1, 0, 0, 1, 0];
const lampAt = (s) => {
  if (s < 0) return 1;
  if (s < 0.8) return STUTTER[Math.floor(s / 0.05)] ? 0.92 : 0.04;
  if (s < 1.3) return 0.86 - (s - 0.8) * 0.2;
  if (s < 1.6) return STUTTER[Math.floor((s - 1.3) / 0.05) + 15] ? 0.55 : 0.02;
  return 0;
};
const GONE_AT = 0.41; // seconds into the stutter

function Station({ p, local, dur, k, zoom }) {
  const s0 = local - 0.8 * dur;
  // Out before the terminal wakes, however short the act is measured.
  const lamp = p >= 0.858 ? 0 : lampAt(s0);
  const here = s0 < GONE_AT;
  const reach = smooth(Math.min(seg(p, 0.736, 0.758), 1 - seg(p, 0.772, 0.796)));
  const P = mixPose(REST, REACH, reach);
  const breathe = Math.sin(local * 1.25) * 1.2;
  // The terminal: a dot, a line, the full raster, a few stutters, then steady.
  const since = local - 0.866 * dur;
  const dot = since >= 0 ? clamp(since / 0.06) : 0;
  const sx = seg(since, 0.05, 0.2);
  const sy = ease.out(seg(since, 0.2, 0.38));
  const stutter = since < 0.4 ? 1 : since < 1.2
    ? (Math.sin(since * 41) > 0.55 ? 0.35 : 1)
    : 0.94 + 0.04 * Math.sin(local * 9.3) + 0.02 * Math.sin(local * 23);
  const power = since <= 0 ? 0 : (since < 0.2 ? 0.6 * dot : 1) * stutter;
  const cold = power * smooth(seg(since, 0.15, 1));
  const blink = since > 1.5 && frac(local / 1.06) < 0.53;
  const secA = (Math.floor(local) + ease.out(clamp(frac(local) / 0.12))) * 6 + 42;
  const glint = 0.5 + 0.5 * Math.sin(local * 0.9);
  const lit = Math.max(lamp, cold * 0.9, 0.14);
  // Under the screen the gold cools but stays gold.
  const goldHi = mixColor(mixColor("#3a2c14", "#fff4cc", lit), C.coldHot, cold * 0.5);
  const goldMid = mixColor(mixColor("#2a1d0a", "#e0ad4c", lit), "#b9b8a4", cold * 0.42);
  const goldLo = mixColor(mixColor("#140d05", "#77501a", lit), "#3a3a36", cold * 0.4);
  const velvet = mixColor(mixColor("#2a0c11", "#4a1820", lamp), "#1c2236", cold * 0.7);
  const wood = mixColor(mixColor("#140f0b", "#5e4632", lamp * 0.85), "#3a4a58", cold * 0.45);
  const rim = mixColor("#000000", "#b9824c", lamp);
  const cx = 720;
  const cy = 500;

  return (
    <g opacity={k} transform={`translate(${cx} ${cy}) scale(${zoom}) translate(${-cx} ${-cy})`}>
      <defs>
        <linearGradient id="gw-st-wall" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="900">
          <stop offset="0" stopColor="#060504" />
          <stop offset="1" stopColor="#0d0a08" />
        </linearGradient>
        <radialGradient id="gw-st-lamp" gradientUnits="userSpaceOnUse" cx={SB.lampX} cy={430} r={780}>
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.36" />
          <stop offset="0.4" stopColor={C.warm} stopOpacity="0.15" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="gw-st-cone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.3" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0.04" />
        </linearGradient>
        <radialGradient id="gw-st-cold" gradientUnits="userSpaceOnUse" cx={TERM.x} cy={TERM.y - 14} r={820}>
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.62" />
          <stop offset="0.14" stopColor={C.cold} stopOpacity="0.3" />
          <stop offset="0.45" stopColor={C.accent} stopOpacity="0.09" />
          <stop offset="1" stopColor={C.coldDeep} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="gw-st-screen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#d6ecfb" />
          <stop offset="0.55" stopColor={C.cold} />
          <stop offset="1" stopColor="#7aa6ca" />
        </linearGradient>
        <radialGradient id="gw-st-case" cx="0.36" cy="0.3">
          <stop offset="0" stopColor={goldHi} />
          <stop offset="0.5" stopColor={goldMid} />
          <stop offset="1" stopColor={goldLo} />
        </radialGradient>
        <linearGradient id="gw-st-band" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={goldLo} />
          <stop offset="0.35" stopColor={goldHi} />
          <stop offset="0.7" stopColor={goldMid} />
          <stop offset="1" stopColor={goldLo} />
        </linearGradient>
        <radialGradient id="gw-st-dial" cx="0.42" cy="0.36">
          <stop offset="0" stopColor={mixColor(mixColor("#3a352c", "#fbf3e1", lit), "#eef7ff", cold)} />
          <stop offset="1" stopColor={mixColor(mixColor("#2a241a", "#d6c39c", lit), "#9fb3c6", cold)} />
        </radialGradient>
        <linearGradient id="gw-st-satin" x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor={mixColor(mixColor("#4a3a2a", "#f1e4c8", lamp), "#d4e4f1", cold * 0.85)} />
          <stop offset="1" stopColor={mixColor(mixColor("#1e160e", "#a88d66", lamp), "#5d7690", cold * 0.85)} />
        </linearGradient>
        <pattern id="gw-peg" width="22" height="22" patternUnits="userSpaceOnUse">
          <circle cx="11" cy="11" r="1.7" fill="#000" fillOpacity="0.55" />
        </pattern>
      </defs>

      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#gw-st-wall)" />
      {/* The line beyond, dark: a column, a bench, a dead lamp */}
      <path d="M 238 -900 V 860 h 22 V -900 Z M 1590 -900 V 860 h 22 V -900 Z" fill="#0b0908" />
      <path d="M -60 596 h 330 v 9 h -330 Z M -40 605 v 250 h 8 v -250 Z M 248 605 v 250 h 8 v -250 Z M 1620 590 h 400 v 9 h -400 Z" fill="#0c0a08" />
      <path d="M 110 -900 V 470 M 1760 -900 V 440" stroke="#0c0a08" strokeWidth={2} />
      <path d="M 82 494 Q 110 462 138 494 Z M 1732 464 Q 1760 432 1788 464 Z" fill="#0c0a08" />

      {/* Lamp light on the wall and bench */}
      {lamp > 0.01 && (
        <>
          <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#gw-st-lamp)" opacity={lamp} />
          <path d={`M ${SB.lampX - 40} ${SB.lampY + 36} L ${SB.lampX + 40} ${SB.lampY + 36} L ${SB.lampX + 360} ${SB.back} L ${SB.lampX - 360} ${SB.back} Z`}
            fill="url(#gw-st-cone)" opacity={lamp} />
        </>
      )}

      {/* Shadow board */}
      <rect x={SB.bx0} y={SB.btop} width={SB.bx1 - SB.bx0} height={SB.back - SB.btop}
        fill={mixColor(mixColor("#100c09", "#382a1e", lamp * 0.75), "#1c2630", cold * 0.4)} />
      <rect x={SB.bx0} y={SB.btop} width={SB.bx1 - SB.bx0} height={SB.back - SB.btop} fill="url(#gw-peg)" />
      <path d={`M ${SB.bx0} ${SB.btop + 0.5} H ${SB.bx1}`} stroke={mixColor("#2a2018", "#8a6a48", lamp)} strokeOpacity={0.6} strokeWidth={2} />
      {TOOLS.map((tl, i) => (
        <g key={i} transform={`translate(${tl.x} ${tl.y}) scale(1.12)`}>
          <path d={tl.d} fill="none" stroke={mixColor(mixColor("#3a3024", "#c9a878", lamp), "#a9c4da", cold)}
            strokeOpacity={0.12 + 0.4 * lamp + 0.45 * cold} strokeWidth={2.6} strokeLinejoin="round" />
          {!tl.empty && <path d={tl.d} fill="#0a0806" transform="scale(0.9)" />}
        </g>
      ))}
      <rect x={858} y={252} width={104} height={26} fill={mixColor(mixColor("#2a2218", "#d8c7a4", lamp), "#9fb3c6", cold * 0.8)} />
      <text x={910} y={269} fill="#2a2018" fontFamily={FONT.mono} fontSize={9.5} letterSpacing="0.2em" textAnchor="middle">STA 17 · B</text>

      {/* Bench */}
      <path d={`M ${SB.x0 + 16} ${SB.back} H ${SB.x1} V ${SB.front} H ${SB.x0} Z`} fill={wood} />
      <rect x={SB.x0} y={SB.front} width={SB.x1 - SB.x0} height={SB.face - SB.front} fill={mixColor("#0b0907", mixColor("#2a1f16", "#18222c", cold), Math.max(lamp * 0.7, cold * 0.6))} />
      <path d={`M ${SB.x0} ${SB.front + 0.5} H ${SB.x1}`} stroke={mixColor(rim, C.cold, cold)} strokeOpacity={0.15 + 0.4 * lamp + 0.5 * cold} strokeWidth={1.6} />
      <path d={`M ${SB.x0 + 22} ${SB.face} V 940 h 16 V ${SB.face} Z M ${SB.x0 + 520} ${SB.face} V 940 h 16 V ${SB.face} Z`} fill="#090706" />

      {/* His hammer, put down */}
      <path d="M 1140 594 L 1290 586" stroke="#0a0806" strokeWidth={9} strokeLinecap="round" />
      <path d="M 1290 566 L 1293 602" stroke="#0a0806" strokeWidth={17} />

      {/* The box */}
      <g>
        <path d={`M ${BOX.x + 6} ${BOX.base - 6} L ${BOX.x + 20} ${BOX.lid + 10} Q ${BOX.x + 22} ${BOX.lid} ${BOX.x + 34} ${BOX.lid} L ${BOX.x + BOX.w - 34} ${BOX.lid} Q ${BOX.x + BOX.w - 22} ${BOX.lid} ${BOX.x + BOX.w - 20} ${BOX.lid + 10} L ${BOX.x + BOX.w - 6} ${BOX.base - 6} Z`}
          fill={velvet} />
        <path d={`M ${BOX.x + 18} ${BOX.base - 8} L ${BOX.x + 30} ${BOX.lid + 16} Q ${BOX.x + 32} ${BOX.lid + 11} ${BOX.x + 40} ${BOX.lid + 11} L ${BOX.x + BOX.w - 40} ${BOX.lid + 11} Q ${BOX.x + BOX.w - 32} ${BOX.lid + 11} ${BOX.x + BOX.w - 30} ${BOX.lid + 16} L ${BOX.x + BOX.w - 18} ${BOX.base - 8} Z`}
          fill="url(#gw-st-satin)" />
        <path d={`M ${BOX.x + 40} ${BOX.lid + 14} L ${BOX.x + 110} ${BOX.lid + 14} L ${BOX.x + 60} ${BOX.base - 10} L ${BOX.x + 26} ${BOX.base - 10} Z`}
          fill="#ffffff" opacity={0.06 + 0.06 * lamp} />
        <rect x={WATCH.x - 50} y={BOX.lid + 24} width={100} height={30} rx={2} fill="url(#gw-st-band)" />
        <rect x={WATCH.x - 47} y={BOX.lid + 27} width={94} height={24} rx={1.5} fill="none" stroke={goldLo} strokeOpacity={0.6} strokeWidth={0.8} />
        <text x={WATCH.x} y={BOX.lid + 37.5} fill={mixColor("#3a2610", "#1b2c3d", cold)} fontFamily={FONT.mono} fontSize={7.5} letterSpacing="0.22em" textAnchor="middle">NO. 0417</text>
        <text x={WATCH.x} y={BOX.lid + 47.5} fill={mixColor("#3a2610", "#1b2c3d", cold)} fontFamily={FONT.mono} fontSize={7.5} letterSpacing="0.12em" textAnchor="middle">1995 – 2025</text>
        {/* cushion, bracelet over it, the watch */}
        <rect x={WATCH.x - 70} y={WATCH.y - 40} width={140} height={86} rx={34} fill="url(#gw-st-satin)" />
        <path d={`M ${WATCH.x - 70} ${WATCH.y - 4} Q ${WATCH.x} ${WATCH.y + 12} ${WATCH.x + 70} ${WATCH.y - 4}`} stroke="#000" strokeOpacity={0.15} strokeWidth={1.5} fill="none" />
        <rect x={WATCH.x - 17} y={WATCH.y - WATCH.r - 28} width={34} height={36} rx={4} fill="url(#gw-st-band)" />
        <rect x={BOX.x + 10} y={BOX.base - 12} width={BOX.w - 20} height={14} fill="url(#gw-st-satin)" />
        <rect x={WATCH.x - 17} y={WATCH.y + WATCH.r - 8} width={34} height={BOX.base + 8 - (WATCH.y + WATCH.r - 8)} fill="url(#gw-st-band)" />
        <path d={LINKS_D} stroke={goldLo} strokeOpacity={0.55} strokeWidth={0.9} />
        <g transform={`translate(${WATCH.x} ${WATCH.y})`}>
          <path d={`M -16 ${-WATCH.r + 2} L -12 ${-WATCH.r - 6} L 12 ${-WATCH.r - 6} L 16 ${-WATCH.r + 2} Z M -16 ${WATCH.r - 2} L -12 ${WATCH.r + 6} L 12 ${WATCH.r + 6} L 16 ${WATCH.r - 2} Z`}
            fill={goldMid} />
          <rect x={WATCH.r - 2} y={-6} width={10} height={12} rx={2} fill={goldMid} />
          <circle r={WATCH.r} fill="url(#gw-st-case)" />
          <circle r={WATCH.r - 4.5} fill={goldLo} />
          <circle r={WATCH.r - 6.5} fill="url(#gw-st-dial)" />
          <path d={DIAL_TICKS_D} stroke={mixColor("#5a3e16", "#2a3a4a", cold)} strokeWidth={1.1} />
          <path d="M 0 4 L 0 -19" stroke="#1a130b" strokeWidth={3.2} strokeLinecap="round" transform="rotate(75.5)" />
          <path d="M 0 5 L 0 -29" stroke="#1a130b" strokeWidth={2.2} strokeLinecap="round" transform="rotate(186)" />
          <path d="M 0 8 L 0 -33" stroke="#9b2c1c" strokeWidth={1} strokeLinecap="round" transform={`rotate(${secA})`} />
          <circle r={2.4} fill="#1a130b" />
          <path d={`M ${-WATCH.r * 0.62} ${-WATCH.r * 0.56} A ${WATCH.r - 2} ${WATCH.r - 2} 0 0 1 ${WATCH.r * 0.42} ${-WATCH.r * 0.78}`}
            stroke="#ffffff" strokeOpacity={0.2 + 0.3 * lamp * glint + 0.35 * cold} strokeWidth={1.8} fill="none" strokeLinecap="round" />
          {cold > 0 && (
            <path d={`M ${-WATCH.r * 0.95} ${WATCH.r * 0.2} A ${WATCH.r - 1} ${WATCH.r - 1} 0 0 1 ${-WATCH.r * 0.5} ${-WATCH.r * 0.85}`}
              stroke={C.coldHot} strokeOpacity={0.7 * cold} strokeWidth={2.2} fill="none" strokeLinecap="round" />
          )}
        </g>
        <rect x={BOX.x} y={BOX.base} width={BOX.w} height={56} rx={5} fill={velvet} />
        <path d={`M ${BOX.x + 6} ${BOX.base + 1.5} H ${BOX.x + BOX.w - 6}`} stroke={goldMid} strokeOpacity={0.55} strokeWidth={1.4} />
        <rect x={WATCH.x - 9} y={BOX.base + 4} width={18} height={9} rx={1.5} fill={goldMid} opacity={0.8} />
        {cold > 0 && (
          <path d={`M ${BOX.x + 1} ${BOX.base + 52} V ${BOX.base + 4} M ${BOX.x + 7} ${BOX.base - 8} L ${BOX.x + 21} ${BOX.lid + 10}`}
            stroke={C.cold} strokeOpacity={0.55 * cold} strokeWidth={1.6} fill="none" />
        )}
      </g>

      {/* Lamp */}
      <path d={`M ${SB.lampX} ${BLEED.y} V ${SB.lampY}`} stroke="#050404" strokeWidth={2.5} />
      <path d={`M ${SB.lampX - 50} ${SB.lampY + 36} Q ${SB.lampX} ${SB.lampY - 18} ${SB.lampX + 50} ${SB.lampY + 36} Z`} fill="#070605" />
      <ellipse cx={SB.lampX} cy={SB.lampY + 36} rx={33} ry={5.5} fill={mixColor("#141009", C.warmHot, lamp)} />

      {/* Him: rim-lit by the lamp, then nobody */}
      {here && (
        <g>
          <Worker x={SB.man + 1.9} y={SB.floor - 1.4 + breathe} s={SB.s} color={rim} pose={P} opacity={0.7} brim="short" />
          <Worker x={SB.man} y={SB.floor + breathe} s={SB.s} color="#050404" pose={P} brim="short" />
        </g>
      )}

      {/* The terminal, where he stood */}
      {since > 0 && (
        <g>
          <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#gw-st-cold)" opacity={cold} />
          <ellipse cx={TERM.x + 30} cy={SB.floor + 6} rx={300} ry={30} fill={C.cold} opacity={0.1 * cold} />
          <path d={`M ${TERM.x - 9} ${SB.floor} V 612 h 18 V ${SB.floor} Z M ${TERM.x - 84} ${SB.floor} h 168 v -9 q -84 -9 -168 0 Z`} fill="#05070a" />
          <path d={`M ${TERM.x - 72} 614 L ${TERM.x + 72} 614 L ${TERM.x + 94} 580 L ${TERM.x - 94} 580 Z`} fill="#0a0e12"
            stroke={C.cold} strokeOpacity={0.4 * cold} strokeWidth={1} />
          <path d={TERM_KEYS_D} stroke={C.cold} strokeOpacity={0.3 * cold} strokeWidth={1.6} />
          <rect x={TERM.x - 15} y={TERM.y + 92} width={30} height={22} fill="#06080b" />
          <rect x={TERM.x - 120} y={TERM.y - 92} width={240} height={186} rx={12} fill="#080b0f"
            stroke={C.cold} strokeOpacity={0.5 * cold} strokeWidth={1.3} />
          <rect x={TERM.x - 104} y={TERM.y - 77} width={208} height={142} rx={5} fill="#02050a" />
          <g transform={`translate(${TERM.x} ${TERM.y - 6}) scale(${Math.max(0.02, sx)} ${Math.max(0.025, sy)})`}>
            <rect x={-104} y={-71} width={208} height={142} rx={5} fill="url(#gw-st-screen)" opacity={power} />
          </g>
          {dot > 0 && since < 0.25 && <circle cx={TERM.x} cy={TERM.y - 6} r={3.5} fill={C.coldHot} opacity={1 - seg(since, 0.15, 0.25)} />}
          {since > 0.9 && (
            <g opacity={cold}>
              <text x={TERM.x - 88} y={TERM.y - 50} fill={C.coldDeep} fontFamily={FONT.mono} fontSize={10.5} letterSpacing="0.2em">LINE B · STA 17</text>
              <path d={`M ${TERM.x - 88} ${TERM.y - 41} H ${TERM.x + 88}`} stroke={C.coldDeep} strokeOpacity={0.45} strokeWidth={1} />
              <text x={TERM.x - 88} y={TERM.y - 12} fill={C.coldDeep} fontFamily={FONT.mono} fontSize={16}>{">"}</text>
              {blink && <rect x={TERM.x - 70} y={TERM.y - 25} width={11} height={17} fill={C.coldDeep} />}
            </g>
          )}
          <circle cx={TERM.x + 98} cy={TERM.y + 80} r={2.8} fill={C.coldHot} opacity={power} />
        </g>
      )}
    </g>
  );
}

// ===========================================================================

export default function GoldWatch({ p, local, dur }) {
  const townK = 1 - smooth(seg(p, 0.285, 0.322));
  const montK = smooth(seg(p, 0.285, 0.322)) * (1 - smooth(seg(p, 0.684, 0.712)));
  const stationK = smooth(seg(p, 0.684, 0.712));
  const montZoom = 1 + 0.18 * ease.in(seg(p, 0.672, 0.712));
  const stationZoom = 0.96 + 0.04 * ease.out(seg(p, 0.684, 0.74)) + 0.1 * smooth(seg(p, 0.74, 1));

  return (
    <g>
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="#000000" />
      {townK > 0 && <Town p={p} local={local} dur={dur} />}
      {montK > 0 && <Montage p={p} local={local} dur={dur} k={montK} zoom={montZoom} />}
      {stationK > 0 && <Station p={p} local={local} dur={dur} k={stationK} zoom={stationZoom} />}
    </g>
  );
}
